/**
 * @file Build Vivaldi URLs.
 */
import arrayToQueryString from './array_to_query_string'
import { EU_INHOUSE_HOST, EU_PROXY_HOST, US_INHOUSE_HOST, US_PROXY_HOST } from './hosts'
import { QueryParam, TransformTuple } from './types'
import { isValidImageDataUrl } from './url_parser'

const version = '1'

const extractGcsBucket = (url: string): string | null => {
  try {
    const { hostname, pathname } = new URL(url)
    const virtualHostedMatch = hostname.match(/^(.+)\.storage\.googleapis\.com$/)
    if (virtualHostedMatch) return virtualHostedMatch[1]
    if (hostname === 'storage.googleapis.com') {
      const segments = pathname.split('/').filter(Boolean)
      return segments[0] ?? null
    }
    return null
  } catch {
    return null
  }
}

const isEuStorageUrl = (url: string): boolean => {
  const bucket = extractGcsBucket(url)
  if (bucket) return bucket.includes('euw3')
  try {
    const { hostname } = new URL(url)
    return hostname.endsWith('-eu.padletusercontent.com')
  } catch {
    return false
  }
}

const buildProxyUrl = (url: string): string => {
  // Return valid image data URLs as-is. If it's arbitrary data, return an empty string instead.
  if (url.startsWith('data:')) {
    return isValidImageDataUrl(url) ? url : ''
  }

  const proxyHost = isEuStorageUrl(url) ? EU_PROXY_HOST : US_PROXY_HOST
  const encodedUrl: string = encodeURIComponent(url)
  return `https://${proxyHost}/${version}/proxy?url=${encodedUrl}`
}

interface TransformUrlOptions {
  transforms: TransformTuple[]
  metadata: boolean
  format?: string
}

const generateVersionFromUrl = (url: string): string => {
  const downcaseUrl = url.toLowerCase()
  if (/(png|jpg|jpeg|webp)$/.exec(downcaseUrl)) return '1'
  if (downcaseUrl.endsWith('gif')) return '2'
  return '3'
}

const buildTransformUrl = (url: string, options: TransformUrlOptions): string => {
  const processingOptionsString: string[] = options.transforms.map((x): string => x.join('_'))
  let version
  const urlQueryParams: QueryParam[] = []
  if (options.metadata) {
    urlQueryParams.push(['m', '1'])
    version = '1'
  } else {
    version = generateVersionFromUrl(url)
  }
  if (processingOptionsString.length > 0) urlQueryParams.push(['t', processingOptionsString.join(',')])
  urlQueryParams.push(['url', url])
  const urlQueryString = arrayToQueryString(urlQueryParams)
  const inhouseHost = isEuStorageUrl(url) ? EU_INHOUSE_HOST : US_INHOUSE_HOST
  if (options.format) {
    return `https://${inhouseHost}/${version}/image.${options.format}?${urlQueryString}`
  }
  return `https://${inhouseHost}/${version}/image?${urlQueryString}`
}

export { buildProxyUrl, buildTransformUrl }
export type { TransformUrlOptions }
